import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Container, Typography, Card, Button, Grid } from 'components/base';
import sample from 'utils/sample';
import useWindowSize from 'components/hooks/useWindowSize';

import useEditorContext from '../context/editorContext';

import {
  getOriginalPage,
  getTransformedPage,
  getMinimumNumberOfPages,
  getMaximumNumberOfPages,
  getBlankPage,
} from 'editor-utils/template';
import Page from '../components/page/preview';
import useEditorRouter from '../lib/editorRouter';
import { generateUpdatedPage, generatePage } from '../generator/utils';
import PageWrapper from '../components/page/wrapper';

import usePhotoWellContext from '../context/photoWellContext';
import usePhotoCropperContext from '../context/photoCropperContext';
import pagesValidator from '../lib/pagesValidator';
import { formatData } from 'editor-utils/dataParser';
import DesignSelect from '../components/designSelect';
import EnvelopeEditor from './envelope';
import QuantitySelect from '../components/quantitySelect';

const SECTION = {
  FRONT: 'front',
  INSIDE: 'inside',
  BACK: 'back',
  ENVELOPE: 'envelope',
};

const SECTIONS = {
  [SECTION.FRONT]: {
    key: SECTION.FRONT,
    label: 'Front',
  },
  [SECTION.INSIDE]: {
    key: SECTION.INSIDE,
    label: 'Inside',
  },
  [SECTION.BACK]: {
    key: SECTION.BACK,
    label: 'Back',
  },
  [SECTION.ENVELOPE]: {
    key: SECTION.ENVELOPE,
    label: 'Envelope',
  },
};

const CardEditorPreview = props => {
  const editorContext = useEditorContext();
  const photoWell = usePhotoWellContext();
  const photoCropper = usePhotoCropperContext();

  const { width } = useWindowSize();

  const editorRouter = useEditorRouter(props.product.id);
  const [activeSection, setActiveSection] = useState(SECTION.FRONT);

  const { photos, pages: pagesData } = props.item;

  const templatePages = useMemo(() => {
    return props.template.pages.reduce(
      (acc, page) => {
        const type = page.id.split('-').shift();

        switch (type ?? page.caption) {
          case 'front':
          case 'Front':
          case 'Front Cover':
            acc.front.push(page);
            break;
          case 'back':
          case 'Back':
          case 'Back Cover':
            acc.back.push(page);
            break;
          default:
            acc.inside.push(page);
            break;
        }
        return acc;
      },
      {
        front: [],
        back: [],
        inside: [],
      },
    );
  }, [props.template]);

  const sections = [SECTIONS[SECTION.FRONT]].reduce((acc, item) => {
    acc.push(item);

    const minimumNumberOfPages = getMinimumNumberOfPages(props.template);
    switch (minimumNumberOfPages) {
      case 2:
        acc.push(SECTIONS[SECTION.BACK]);
        break;
      default:
        acc.push(SECTIONS[SECTION.INSIDE], SECTIONS[SECTION.BACK]);
        break;
    }

    if (props.item.upsell) {
      acc.push(SECTIONS[SECTION.ENVELOPE]);
    }

    return acc;
  }, []);

  const withEnvelopeEditor = useMemo(() => {
    if (sections.find(section => section.key === SECTION.ENVELOPE)) {
      return true;
    }
    return false;
  }, [sections]);

  const pages = useMemo(() => {
    const minimumNumberOfPages = getMinimumNumberOfPages(props.template);
    const maximumNumberOfPages = getMaximumNumberOfPages(props.template);
    if (pagesData.length < minimumNumberOfPages || pagesData.length > maximumNumberOfPages) {
      // TODO simple generator
      const data = [
        generatePage(templatePages.front[0]),
        ...new Array(minimumNumberOfPages - 2)
          .fill(null)
          .map((val, index) =>
            generatePage(
              index < templatePages.inside.length
                ? templatePages.inside[index]
                : sample(templatePages.inside),
            ),
          ),
        generatePage(getBlankPage(templatePages.back)),
      ];
      editorContext.setPages(props.item.id, data);
      return data;
    }
    return pagesData;
  }, [props.template, pagesData, templatePages]);

  const handleOnChangePage = (index, pageId) => {
    const newPage = generateUpdatedPage(props.template, pagesData[index], pageId);

    editorContext.changePage(props.item.id, newPage, { pageIndex: index });
  };

  const handleOnAddToCartClick = async e => {
    e.preventDefault();

    const validator = pagesValidator(props.template, props.item.pages, props.config);
    const data = formatData(props.template, props.item, { validator });

    if (validator.isValid()) {
      await props.onFinish(data);
      return;
    }

    const errors = validator.getErrors();
    if (errors.length > 0) {
      // TODO show all warnings
      editorContext.toast(errors[0].text, {
        type: 'warning',
      });
    }
  };

  const handleOnPagePhotoUpload = (pageIndex, placeholder) => {
    photoWell.show({
      uploadEnabled: true,
      onSelectPhotos: selectedPhotos => {
        editorContext.addPhotoToPlaceholder(
          props.item.id,
          pageIndex,
          placeholder.id,
          selectedPhotos[0],
        );
        photoWell.hide();
      },
    });
  };

  const handleOnPagePhotoEdit = (pageIndex, placeholder, photo) => {
    photoCropper.show({
      placeholder,
      photo,
      onSuccess: data => {
        editorContext.setPhotoData(props.item.id, pageIndex, placeholder.id, data);
      },
    });
  };

  const handleOnPageTextChange = (pageIndex, placeholder, value, override) => {
    editorContext.setTextData(props.item.id, pageIndex, placeholder.id, value, override);
  };

  const changeUpsellItem = upsellItem => {
    editorContext.updateUpsell(props.item.id, upsellItem);
  };

  const pageComponents = pages.reduce(
    (acc, page, index) => {
      const originalPage = getOriginalPage(props.template, page, 'pages');

      let otherPages = templatePages.inside;
      let pageLabel = `Inside Page ${index}`;
      let buttonPosition = index % 2 === 0 ? 'right' : 'left';
      if (index === 0) {
        otherPages = templatePages.front;
        pageLabel = 'Front Page';
        buttonPosition = 'right';
      } else if (index === pages.length - 1) {
        otherPages = templatePages.back;
        pageLabel = 'Back Page';
        buttonPosition = 'right';
      }

      const pageWrapper = (
        <PageWrapper
          page={originalPage}
          buttonPosition={buttonPosition}
          otherPages={otherPages.map(otherPage => ({
            id: otherPage.id,
            preview: (
              <Page
                page={getTransformedPage(props.template, originalPage, otherPage.id)}
                template={props.template}
                photos={photos}
                fullWidth
                preview
              />
            ),
          }))}
          showAlwaysOtherPages={width >= 900}
          otherPagesLabel="LAYOUTS"
          onChangePage={id => handleOnChangePage(index, id)}
          fullWidth
        >
          <Page
            page={originalPage}
            photos={photos}
            template={props.template}
            onPhotoUpload={(...data) => handleOnPagePhotoUpload(index, ...data)}
            onPhotoEdit={(...data) => handleOnPagePhotoEdit(index, ...data)}
            onTextChange={(...data) => handleOnPageTextChange(index, ...data)}
            fullWidth
            interactive
            bleed={[0.3175, 0.3175, 0.3175, 0.3175]}
          />
        </PageWrapper>
      );

      const pageComponent = colWidth => (
        <Grid.Col key={page.key} xs={colWidth} className="col-page-preview">
          {pageWrapper}
          <Typography.H6>{pageLabel}</Typography.H6>
        </Grid.Col>
      );

      const isPortrait = originalPage.size.width < originalPage.size.height;

      if (index === 0) {
        acc.front = pageComponent(isPortrait ? 8 : 12);
      } else if (index === pages.length - 1) {
        acc.back = pageComponent(isPortrait ? 8 : 12);
      } else {
        acc.inside.push(pageComponent(isPortrait ? 6 : 12));
      }

      return acc;
    },
    {
      front: null,
      back: null,
      inside: [],
    },
  );

  return (
    <Container>
      <Card className="text-center mx-auto mw-480 mb-5">
        <Card.Body>
          <Typography.H4 className="text-warning">PREVIEW YOUR CREATION</Typography.H4>
          <Typography.P className="mb-1">
            Preview your photo card below. To edit, zoom, crop, or replace a photo, tap the photo.
            Please note, your card will be trimmed around the edges once printed, so please avoid
            placing the subjects of your photos too close to the edges.
          </Typography.P>
          <QuantitySelect product={props.product} productType="Card" />
          <Grid.Row>
            <Grid.Col sm={6}>
              <Button
                block
                type="default"
                onClick={() => {
                  editorRouter.goTo(`/${props.product.id}`);
                }}
              >
                Start Over
              </Button>
            </Grid.Col>
            <Grid.Col sm={6}>
              <Button block type="success" onClick={handleOnAddToCartClick}>
                Add to Cart
              </Button>
            </Grid.Col>
          </Grid.Row>
        </Card.Body>
      </Card>

      <DesignSelect
        active={activeSection}
        onChange={setActiveSection}
        items={sections.map(section => ({
          key: section.key,
          title: section.label.toUpperCase(),
          icon: `/static/img/photo-card-${section.key}.png`,
        }))}
        className="design-select-no-padding mb-3"
      />

      <div className="text-center mx-auto mw-800">
        <Grid.Row noGutters className="mb-3">
          {activeSection === SECTION.FRONT && (
            <>
              <Grid.Col xs={2} className="col-page-preview" />
              {pageComponents.front}
              <Grid.Col xs={2} className="col-page-preview" />
            </>
          )}

          {activeSection === SECTION.INSIDE && <>{pageComponents.inside}</>}

          {activeSection === SECTION.BACK && (
            <>
              <Grid.Col xs={2} className="col-page-preview" />
              {pageComponents.back}
              <Grid.Col xs={2} className="col-page-preview" />
            </>
          )}

          {withEnvelopeEditor && (
            <EnvelopeEditor
              productId={props.item.upsell.id}
              version={props.item.upsell.version}
              data={props.item.upsell.data}
              onChange={changeUpsellItem}
              active={activeSection === SECTION.ENVELOPE}
              quantity={parseInt(editorRouter.query.quantity, 10)}
              template={props.template}
            />
          )}
        </Grid.Row>
      </div>
    </Container>
  );
};

CardEditorPreview.propTypes = {
  item: PropTypes.object,
  params: PropTypes.arrayOf(PropTypes.string),
  config: PropTypes.object,
  product: PropTypes.object,
  template: PropTypes.object,
  onFinish: PropTypes.func,
};

export default CardEditorPreview;
